import React from 'react';
import styled, { css } from 'styled-components';
import { UeContainer, UeElement, UeReference } from '@oneaudi/falcon-tools';
import { parseAssetValue } from '../../utils/parseAssetValue';
import type { AssetTypeImage } from '../../../types';
import { Mode } from '../../../types';
import { useWindowSize } from '../../hooks';
import { lgWidth, mdWidth, xxlWidth, xlWidth, xsWidth } from '../../utils/theme';

const preserveVideoWidthStyles = css`
  min-height: auto;
  height: auto;
`;

const headerHeightOffset = '72px';

export const ResponsiveMediaItemBase = styled.div<{
  mode: Mode;
  preserveVideoWidth?: boolean;
}>`
  object-fit: ${(preserveVideoWidth) => (preserveVideoWidth ? 'contain' : 'cover')};
  height: ${({ mode }) => (mode === Mode.LARGE ? `calc(100vh - ${headerHeightOffset})` : '100%')};
  @media (min-width: ${xsWidth}px) {
    ${({ preserveVideoWidth, mode }) =>
      preserveVideoWidth
        ? preserveVideoWidthStyles
        : css`
            min-height: ${mode === Mode.LARGE ? `calc(640px - ${headerHeightOffset})` : 'auto'};
            height: ${mode === Mode.SMALL ? '640px' : `calc(100vh - ${headerHeightOffset})`};
          `}
  }
  @media (min-width: ${mdWidth}px) {
    ${({ preserveVideoWidth, mode }) =>
      preserveVideoWidth
        ? preserveVideoWidthStyles
        : css`
            min-height: ${mode === Mode.LARGE ? `calc(800px - ${headerHeightOffset})` : 'auto'};
            height: ${mode === Mode.SMALL ? '800px' : `calc(100vh - ${headerHeightOffset})`};
          `}
  }
  @media (min-width: ${lgWidth}px) {
    ${({ preserveVideoWidth, mode }) =>
      preserveVideoWidth
        ? preserveVideoWidthStyles
        : css`
            min-height: ${mode === Mode.LARGE ? `calc(640px - ${headerHeightOffset})` : 'auto'};
            height: ${mode === Mode.SMALL ? '640px' : `calc(100vh - ${headerHeightOffset})`};
          `}
  }
  @media (min-width: ${xxlWidth}px) {
    ${({ preserveVideoWidth, mode }) =>
      preserveVideoWidth
        ? preserveVideoWidthStyles
        : css`
            min-height: ${mode === Mode.LARGE ? `calc(800px - ${headerHeightOffset})` : 'auto'};
            height: ${mode === Mode.SMALL ? '800px' : `calc(100vh - ${headerHeightOffset})`};
          `}
  }
`;

const MediaItem = styled(ResponsiveMediaItemBase)`
  width: auto;
  position: relative;
`;

const Image = styled.img`
  height: 100%;
  object-fit: cover;
  position: relative;
  width: 100%;
`;

interface StageImageProps {
  asset: AssetTypeImage;
  mode: Mode;
}

const StageImage = ({ asset, mode }: StageImageProps) => {
  const { landscapeAltText, portraitAltText, portrait, landscape } = asset;
  const { width } = useWindowSize();
  const imagePortrait = encodeURI(parseAssetValue(portrait?.assetValue ?? '').src);
  const imageLandscape = encodeURI(parseAssetValue(landscape?.assetValue ?? '').src);

  const isMobile = width < Number(mdWidth);

  return (
    <UeContainer propertyPath="asset" label="images">
      <MediaItem mode={mode} data-testid="fa-editorial-stage-image" className="mediaItem">
        <UeReference propertyPath="asset" behavior="component">
          {(ueProps) => (
            <picture {...ueProps}>
              {/* < 768 */}
              <UeElement type="media" property="assetPortrait" label="imagePortrait">
                {(ueElemProps) => (
                  <source
                    media={`(max-width: ${mdWidth}px)`}
                    srcSet={`${imagePortrait}?width=${mdWidth}`}
                    {...ueElemProps}
                  />
                )}
              </UeElement>
              {/* < 1024 */}
              <UeElement type="media" property="assetLandscape" label="imageLandscape">
                {(ueElemProps) => (
                  <source
                    media={`(max-width: ${lgWidth}px)`}
                    srcSet={`${imageLandscape}?width=${lgWidth}`}
                    {...ueElemProps}
                  />
                )}
              </UeElement>
              {/* < 1440 */}
              <UeElement type="media" property="assetLandscape" label="imageLandscape">
                {(ueElemProps) => (
                  <source
                    media={`(max-width: ${xlWidth}px)`}
                    srcSet={`${imageLandscape}?width=${xlWidth}`}
                    {...ueElemProps}
                  />
                )}
              </UeElement>
              {/* >= 1440 */}
              <UeElement type="media" property="assetLandscape" label="imageLandscape">
                {(ueElemProps) => (
                  <source
                    media={`(min-width: ${xlWidth}px)`}
                    srcSet={`${imageLandscape}?width=${xxlWidth}`}
                    {...ueElemProps}
                  />
                )}
              </UeElement>
              {/* fallback */}
              {/* TODO: remove this portion ? */}
              <UeElement
                type="media"
                property={isMobile ? 'assetPortrait' : 'assetLandscape'}
                label={isMobile ? 'imagePortrait' : 'imageLandscape'}
              >
                {(ueElemProps) => (
                  <Image
                    alt={isMobile ? portraitAltText : landscapeAltText}
                    data-object-fit
                    src={imagePortrait}
                    loading="lazy"
                    {...ueElemProps}
                  />
                )}
              </UeElement>
            </picture>
          )}
        </UeReference>
      </MediaItem>
    </UeContainer>
  );
};

export default StageImage;
